import type { ReactNode } from 'react';

import { Infoblock } from '../infoblock/infoblock';
import { TextList } from '../text-list/text-list';

// This must be kept in sync with the shape in Blurr
export interface ValidationProps {
  valid: boolean;
  messages: Array<ReactNode>;
}

export const normaliseValidation = (
  validation: ValidationProps | Array<ValidationProps | undefined>,
) => {
  if (Array.isArray(validation)) {
    // in compound components we may have multiple validations
    // and some may be undefined at any given time
    const onlyProps = validation.filter(
      (props): props is ValidationProps => props !== undefined,
    );

    return {
      valid: onlyProps.every(({ valid }) => valid) || false,
      messages: onlyProps
        .map(({ valid, messages }) => (valid ? [] : messages))
        .flat(),
    };
  }
  return validation;
};

/**
 * Creates a unique id for the error message to tie the field with the message
 */
const createErrorMessageId = (name: string) => `${name}-error`;

/**
 * Utility to turn validation into aria props for fields.
 */
export const validationToFieldAria = (
  name: string,
  validation?: ValidationProps | Array<ValidationProps | undefined>,
) => {
  if (!validation) return {};

  const { messages, valid } = normaliseValidation(validation);

  return {
    'aria-invalid': !valid,
    'aria-errormessage':
      !valid && messages.length > 0 ? createErrorMessageId(name) : undefined,
  };
};

export const ValidationMessages = ({
  errors,
  id,
}: {
  errors: string[] | undefined;
  id: string;
}) => {
  if (!errors?.length) return null;
  return (
    <Infoblock variant="error" id={id} role="alert">
      {errors.length === 1 ? (
        errors[0]
      ) : (
        <TextList variant="caption">
          {errors.map((message, index) => (
            <TextList.Item key={index}>{message}</TextList.Item>
          ))}
        </TextList>
      )}
    </Infoblock>
  );
};

export const ValidationMessagesLegacy = ({
  validation,
  showValidation,
  name,
}: {
  validation: ValidationProps | Array<ValidationProps | undefined>;
  showValidation: boolean;
  name: string;
}) => {
  if (!showValidation || !validation) return null;

  const { messages } = normaliseValidation(validation);

  if (messages.length === 0) return null;

  // the id and role are used to tie this error message with the invalid field
  return (
    <Infoblock variant="error" id={createErrorMessageId(name)} role="alert">
      {messages.length === 1 ? (
        messages[0]
      ) : (
        <TextList variant="caption">
          {messages.map((message, index) => (
            <TextList.Item key={index}>{message}</TextList.Item>
          ))}
        </TextList>
      )}
    </Infoblock>
  );
};
